import { roundAllCorners } from "../model/constructionRounding";
import paper from "paper";
import { ToolBase } from "./ToolBase";
import { computeDefaultSmoothAnchors } from "../model/constructionAnchors";
import type { PointerSample } from "../canvas/PointerInput";
import { createPiece, nextPartName, type Piece } from "../model/Project";
import { withPiece } from "../model/projectOps";
import { pathFromBuffer, type Scene } from "../canvas/Scene";
import { smoothPathFromRawPoints } from "../model/freehandSmoothing";
import { DEFAULT_LEATHER_SMOOTHNESS } from "../model/leatherSmoothing";
import { cssVar } from "../colors";
import { DrawGestureEngine, type DrawGestureHost } from "./drawGestureEngine";
import {
  angleModifierHeld,
  constrainSquare,
  isAngleSnapActive,
  isCenterSnapActive,
  isSizesSnapActive,
  resolveDragBox,
  resolveDragAxis,
  snapToCommonAngle,
  type Point2,
} from "./dragSnap";
import {
  gatherDrawSnapTargets,
  resolveDrawSnap,
  type DrawSnapResult,
  type DrawSnapTargets,
} from "./drawSnap";
import { setAngleHint } from "../snapAngleIndicator";
import {
  RegularPolygonGesture,
  type PolygonPreviewShape,
  type Vec2,
} from "./regularPolygonGesture";
import {
  CLOSE_SNAP_RADIUS,
  closeSnapRadius,
  emitSegmentRay,
} from "./drawGesture";
import {
  makeSlotPathData,
  SLOT_DEFAULT_LENGTH_RATIO,
  SLOT_DEFAULT_WIDTH_MM,
} from "./CutoutTool";
import { DimensionLabel } from "../canvas/DimensionLabel";
import {
  offsetSourceOutlines,
  OutlineOffsetController,
  type OffsetCandidate,
  type OutlineOffsetHost,
} from "./outlineOffsetController";
import { showCanvasToast } from "../canvasToast";

export type ShapeKind =
  | "offset"
  | "rect"
  | "ellipse"
  | "freehand"
  | "polygon"
  | "slot";

/**
 * Draws a new piece outline. Rect/ellipse drag from start to current; freehand
 * appends points; polygon clicks-to-add until double-click or Enter.
 */
export class ShapeTool
  extends ToolBase
  implements DrawGestureHost, OutlineOffsetHost
{
  id = "shape";
  label = "Leather";
  shortcut = "l";
  cursor = "crosshair";
  kind: ShapeKind = "rect";

  /** Shared click-to-add polygon gesture (#804 IE3). This tool OWNS the engine
   *  and implements `DrawGestureHost`; only the polygon kind drives it. The
   *  drag-out kinds (rect / ellipse / slot / freehand) stay tool-owned. */
  private drawEngine = new DrawGestureEngine(this);

  /** Shared regular-polygon gesture (#928/#931/#934) — the same state machine
   *  the construction Draw tool runs (auto-n two-stage flow, click-move-click,
   *  pinned-size aim stage, pinned-circle variable count). Ghosts render in
   *  the leather accent style; commits land as new parts. */
  private polyGesture = new RegularPolygonGesture({
    getScope: () => this.ctx.scene.getPaperScope(),
    getScene: () => this.ctx.scene,
    getViewportView: () => this.ctx.viewport.view,
    getSettings: () => this.ctx.settings,
    renderPreview: (shape) => this.renderPolygonGhost(shape),
    clearPreview: () => this.cleanupPreview(),
    commitVertices: (pts) => this.commitPolygonVertices(pts),
    notifyDraftChange: () => this.drawEngine.notifyDraftChange(),
    liveSides: (sides, radiusMm) => this.polygonLiveCb?.(sides, radiusMm),
    liveSideLength: (mm) => this.polygonLiveSideLenCb?.(mm),
    liveRadius: (mm) => this.polygonLiveRadiusCb?.(mm),
    liveClear: () => this.polygonLiveClearCb?.(),
  });
  /** Live panel mirror for the polygon gesture (#931/#934) — injected by
   *  app.ts, same contract as the construction tool's. */
  private polygonLiveCb: ((sides: number, radiusMm: number) => void) | null =
    null;
  private polygonLiveSideLenCb: ((mm: number) => void) | null = null;
  private polygonLiveRadiusCb: ((mm: number) => void) | null = null;
  private polygonLiveClearCb: (() => void) | null = null;

  /** Re-render an in-flight regular-polygon stage after a panel value edit
   *  (#934) — app.ts calls this on settings changes. */
  refreshPolygonGesture(): void {
    this.polyGesture.refresh();
  }

  setPolygonLiveCallbacks(
    live: (sides: number, radiusMm: number) => void,
    liveSideLength: (mm: number) => void,
    liveRadius: (mm: number) => void,
    clear: () => void,
  ): void {
    this.polygonLiveCb = live;
    this.polygonLiveSideLenCb = liveSideLength;
    this.polygonLiveRadiusCb = liveRadius;
    this.polygonLiveClearCb = clear;
  }

  /** Ghost renderer for the shared polygon gesture — the same thin dashed
   *  accent stroke every leather drag preview uses. */
  private renderPolygonGhost(shape: PolygonPreviewShape): void {
    const scope = this.ctx.scene.getPaperScope();
    this.cleanupPreview();
    const item =
      shape.kind === "line"
        ? new scope.Path({
            segments: [
              new scope.Point(shape.a.x, shape.a.y),
              new scope.Point(shape.b.x, shape.b.y),
            ],
          })
        : new scope.Path({
            segments: shape.vertices.map((v) => new scope.Point(v.x, v.y)),
            closed: true,
          });
    item.strokeColor = new scope.Color(cssVar("--canvas-accent"));
    item.strokeWidth = 0.75;
    item.dashArray = [4, 2];
    item.fillColor = null;
    item.data = { ephemeral: true };
    this.previewItem = item;
  }

  /** Commit a finished regular-polygon ring as a new part — same sink the
   *  click-to-add `commitDraft` uses. */
  private commitPolygonVertices(pts: Vec2[]): void {
    const data = pathFromBuffer(pts.map((p) => ({ x: p.x, y: p.y }))) + " Z";
    const piece = this.newPart(nextPartName(this.ctx.history.state), data);
    this.snapTargets = null; // geometry changed — rebuild targets next use
    // Land the polygon where it was drawn — no auto-arrange (#738). Stay on
    // the Shape tool so the user can keep drawing (overlaps are allowed).
    this.ctx.commit("Create polygon", (proj) => withPiece(proj, piece));
    this.ctx.setSelection({ pieceId: piece.id, decorationId: null });
  }

  private startPt: paper.Point | null = null;
  private currentPt: paper.Point | null = null;
  private buffer: { x: number; y: number }[] = [];
  /** Smoothness seeded onto the next freehand part (#1016) — the tool-level
   *  default, mirroring construction's Draw-tool preset (#416). */
  private freehandSmoothness = DEFAULT_LEATHER_SMOOTHNESS;

  /** Tool-level default for the next freehand trace (#1016), set from the tool
   *  settings panel and persisted across sessions. */
  setFreehandSmoothness(v: number) {
    this.freehandSmoothness = v;
  }
  /** Snap targets (existing vertices / hole centres) gathered lazily at the
   *  start of a draw gesture and reused until the project changes (#761). */
  private snapTargets: DrawSnapTargets | null = null;
  private previewItem: paper.Item | null = null;
  /** Most-recent pointer sample / project-space cursor from
   *  `onMove`. Captured so `onModifiersChanged` can re-fire the
   *  preview when Shift is pressed mid-drag (without waiting for
   *  the next real mouse-move event). */
  private lastSample: PointerSample | null = null;
  private lastP: { x: number; y: number } | null = null;
  /** Live "W × H" readout shown over the rect / ellipse drag preview.
   *  Same colour as the dashed outline below so the two read as one
   *  overlay. */
  private dimensionLabel = new DimensionLabel(cssVar("--canvas-accent"));
  /** Pill (a.k.a. "slot") width in project mm. Drag distance sets length;
   *  click-without-drag produces a horizontal pill `slotWidth × ratio` long. */
  private slotWidth = SLOT_DEFAULT_WIDTH_MM;
  /** User-locked bounding-box dimensions for rect/ellipse. `null` means
   *  the dimension comes from the live cursor delta instead. When both
   *  are non-null, a click places a fixed-size shape (no drag needed). */
  private presetWidth: number | null = null;
  private presetHeight: number | null = null;
  /** Corner radius (mm) a rectangle is DRAWN with (#1069) — set by a picked
   *  standard size that is made with rounded corners, or typed in. Its own
   *  preset, separate from the Round tool's, which arms a click on an EXISTING
   *  corner. Null draws sharp corners. */
  private drawCornerRadius: number | null = null;
  /** Whether the in-flight rect / ellipse drag grows symmetrically about
   *  its start point ("draw from centre"). Latched per pointer sample in
   *  `onDown` / `onMove` from {@link isCenterSnapActive} (Alt held or the
   *  centre snap toggle) so `resolveBoundingBox` — which has no sample —
   *  can read it. */
  private drawFromCenter = false;
  /** Fired during onMove so the properties panel can mirror the in-progress
   *  drag in its inputs. Receives the effective (preset-or-cursor) dimensions
   *  in project mm. Cleared when the drag finishes. */
  private liveDimsCallback?: (w: number, h: number) => void;
  /** The Offset shape kind's gesture (#972), shared with CutoutTool. */
  private offset = new OutlineOffsetController(this);

  setKind(kind: ShapeKind) {
    this.kind = kind;
    this.offset.cancel();
    this.cancelDraft();
  }

  setSlotWidth(width: number) {
    this.slotWidth = width;
  }

  /** Set the corner radius the next rectangle is drawn with (#1069). */
  setDrawCornerRadius(mm: number | null) {
    this.drawCornerRadius = mm;
  }

  setPresetWidth(w: number | null) {
    this.presetWidth = w;
  }

  setPresetHeight(h: number | null) {
    this.presetHeight = h;
  }

  setLiveDimsCallback(fn: ((w: number, h: number) => void) | undefined) {
    this.liveDimsCallback = fn;
  }

  override onActivate() {
    this.snapTargets = null;
    this.cancelDraft();
  }

  override onDeactivate() {
    // Drops any in-flight offset drag AND the hover arrow, so the glyph never
    // outlives the tool that drew it (#972).
    this.offset.cancel();
    this.cancelDraft();
    setAngleHint(null);
    this.ctx.scene.setDrawSnapPoint(null);
    this.ctx.scene.clearGuideRays();
  }

  /** Snap a raw cursor point onto a nearby vertex / the grid and light the
   *  in-draw snap indicator. Targets are cached until the project changes. */
  private snap(p: Point2): DrawSnapResult {
    if (!this.snapTargets) {
      const scope = this.ctx.scene.getPaperScope();
      this.snapTargets = gatherDrawSnapTargets(
        this.ctx.history.state,
        scope,
        this.ctx.scene.getPartLayoutView(),
      );
    }
    const result = resolveDrawSnap(this.ctx, p, this.snapTargets);
    this.ctx.scene.setDrawSnapPoint(result.hit?.pos ?? null);
    return result;
  }

  override onDown(s: PointerSample, p: { x: number; y: number }) {
    const scope = this.ctx.scene.getPaperScope();
    // Offset kind (#972): a press on an existing part edge grabs it and the
    // drag lays a parallel outline. Nothing else in this tool runs while the
    // gesture owns the pointer.
    if (this.kind === "offset") {
      this.offset.handleDown(p);
      return;
    }
    // Regular-polygon stages (#931/#934): the press commits the aimed /
    // previewed polygon, or ends the click-move-click first side.
    if (this.kind === "polygon" && this.polyGesture.press(s, p)) return;
    if (
      this.kind === "polygon" &&
      !(this.drawEngine.vertexCount === 0 && this.regularPolygonMode(s))
    ) {
      // Click-to-add-vertices gesture, driven by the shared draw engine (#804
      // IE3). Close-snap, the vertex/angle/grid snap tiers, and the commit are
      // injected via the `DrawGestureHost` hooks below. Regular-polygon mode
      // (#928) instead falls through to the drag-out flow below.
      this.drawEngine.onDown(s, p);
      return;
    }
    const start = this.snap(p).point;
    const pt = new scope.Point(start.x, start.y);
    this.startPt = pt;
    this.currentPt = pt;
    this.drawFromCenter = this.shouldSnapCenter(s);
    if (this.kind === "freehand") {
      this.buffer = [{ x: pt.x, y: pt.y }];
    }
  }

  override onMove(s: PointerSample, p: { x: number; y: number }) {
    if (this.kind === "offset") {
      this.offset.handleMove(p);
      return;
    }
    this.lastSample = s;
    this.lastP = p;
    if (this.kind === "polygon" && !this.startPt) {
      // Polygon vertices are placed on onDown; a press-drag is a no-op for the
      // leather tools (no bezier shaping). Delegate to keep the engine's flow.
      // A regular-polygon drag (#928) is in flight only while `startPt` is set.
      this.drawEngine.onMove(s, p);
      return;
    }
    this.drawFromCenter = this.shouldSnapCenter(s);
    const scope = this.ctx.scene.getPaperScope();
    let pt: { x: number; y: number } = p;
    if (this.kind === "freehand") {
      // Freehand doesn't snap intermediate samples — it would fight the
      // smoothing. Only the start vertex (in onDown) snaps.
      this.ctx.scene.setDrawSnapPoint(null);
    } else {
      const snap = this.snap(p);
      if (snap.hit?.kind === "vertex") {
        // A nearby existing vertex wins over angle / size snap.
        pt = snap.point;
        if (this.kind === "slot") setAngleHint(null);
      } else if (this.startPt) {
        let applied = false;
        if (
          (this.kind === "rect" || this.kind === "ellipse") &&
          this.shouldSnapSizes(s)
        ) {
          pt = constrainSquare(this.startPt, p);
          applied = true;
        } else if (this.kind === "slot") {
          if (this.shouldSnapAngle(s)) {
            // Snap the pill's drag axis to common drafting angles, just like
            // the cutout slot — and push the angle hint so the snap-angle chip
            // lights up.
            const sn = snapToCommonAngle(this.startPt, p);
            pt = { x: sn.x, y: sn.y };
            setAngleHint(sn.angleRad);
            applied = true;
          } else {
            // Slot drag without snap → no angle to advertise.
            setAngleHint(null);
          }
        }
        // Line (#782) / grid snap only when no vertex / angle / size snap took
        // the point — both are positional snaps that yield to a held modifier.
        if (
          !applied &&
          (snap.hit?.kind === "line" || snap.hit?.kind === "grid")
        )
          pt = snap.point;
      } else if (this.kind === "slot") {
        setAngleHint(null);
      }
    }
    this.currentPt = new scope.Point(pt.x, pt.y);
    // #747: show the slot's drag axis as a length + angle ray while dragging.
    if (this.kind === "slot") {
      if (this.startPt) {
        emitSegmentRay(this.ctx.scene, this.startPt, this.currentPt);
      } else {
        this.ctx.scene.setGuideRays(null);
      }
    }
    if (this.kind === "freehand") {
      const last = this.buffer[this.buffer.length - 1];
      if (!last || Math.hypot(p.x - last.x, p.y - last.y) > 1.0) {
        this.buffer.push({ x: p.x, y: p.y });
      }
    }
    // Push the in-progress bounding-box size into the properties panel so the
    // width/height inputs mirror the drag (and stay frozen on whichever axis
    // is preset-locked). Only rect/ellipse use the dim inputs.
    if (
      (this.kind === "rect" || this.kind === "ellipse") &&
      this.startPt &&
      this.currentPt &&
      this.liveDimsCallback
    ) {
      const box = this.resolveBoundingBox(this.startPt, this.currentPt);
      this.liveDimsCallback(box.width, box.height);
    }
    this.updatePreview();
  }

  override onHover(s: PointerSample, p: { x: number; y: number }) {
    // Capture for `onModifiersChanged` — the staged polygon hovers are
    // modifier-sensitive (Ctrl/Cmd angle snap), like the drag previews.
    this.lastSample = s;
    this.lastP = p;
    // Offset kind (#972): the construction / stitch offset cursor treatment —
    // plain pointer plus the perpendicular-arrow affordance over an edge.
    if (this.kind === "offset") {
      this.offset.handleHover(p);
      return;
    }
    // Regular-polygon stages (#931/#934): the hover rotates the aimed
    // polygon, rubber-bands the click-move-click side, or drives the auto-n
    // stage-2 preview.
    if (this.kind === "polygon" && this.polyGesture.hover(s, p)) return;
    if (this.kind === "polygon" && !this.startPt) {
      // Rubber-band hover, engine-owned flow (#804 IE3). The vertex/angle/grid
      // snap tiers + close-snap ring are injected via `resolveDraftHover` /
      // `onEmptyDraftHover`.
      this.drawEngine.onHover(s, p);
      // While hovering to close, the pending segment IS the closing edge back
      // to the start — the leather tools suppress its ray (the engine emits it
      // unconditionally; clear it after) (#779 / #747).
      if (this.drawEngine.isHoveringToClose()) this.ctx.scene.clearGuideRays();
      return;
    }
    // Pre-press feedback (all kinds): light the snap indicator at the cursor so
    // the user can see the *start* point will snap, not just the drag end.
    this.snap(p);
  }

  /** Draw the on-canvas segment ray from `from` to `to` showing its length +
   *  angle (#747; angle-only feedback was #729). Clears when degenerate. */
  private shouldSnapAngle(s: PointerSample): boolean {
    return isAngleSnapActive(this.ctx.settings, angleModifierHeld(s));
  }
  private shouldSnapSizes(s: PointerSample): boolean {
    return isSizesSnapActive(this.ctx.settings, s.shiftKey);
  }
  private shouldSnapCenter(s: PointerSample): boolean {
    return isCenterSnapActive(this.ctx.settings, s.altKey);
  }

  override onUp(s: PointerSample, p: { x: number; y: number }) {
    if (this.kind === "offset") {
      this.offset.handleUp();
      return;
    }
    if (this.kind === "polygon" && !this.startPt) {
      // Vertices are placed and the loop is closed in `onDown` (click near the
      // first vertex) — except for a pointer with no hover, whose press only
      // previews and whose RELEASE places the vertex (#1116).
      this.drawEngine.onUp(s, p);
      return;
    }

    let pathData: string | null = null;
    let freehandTrace: Vec2[] | null = null;
    let rounded = false;
    const scope = this.ctx.scene.getPaperScope();
    if (this.kind === "polygon" && this.startPt && this.currentPt) {
      // Regular-polygon release (#928/#931/#934): the shared gesture commits
      // the fixed-count ring or transitions into a stage (auto-n stage 2 /
      // click-move-click anchor / pinned-size aim anchor).
      const a = { x: this.startPt.x, y: this.startPt.y };
      const b = { x: this.currentPt.x, y: this.currentPt.y };
      const fromCenter = this.drawFromCenter;
      this.startPt = null;
      this.currentPt = null;
      this.buffer = [];
      this.drawFromCenter = false;
      setAngleHint(null);
      this.ctx.scene.setDrawSnapPoint(null);
      this.polyGesture.release(a, b, fromCenter);
      return;
    } else if (this.kind === "rect" && this.startPt && this.currentPt) {
      const box = this.resolveBoundingBox(this.startPt, this.currentPt);
      if (box.width > 1 && box.height > 1) {
        const right = box.left + box.width;
        const bottom = box.top + box.height;
        pathData = `M${box.left} ${box.top} L${right} ${box.top} L${right} ${bottom} L${box.left} ${bottom} Z`;
        rounded = true;
      }
    } else if (this.kind === "ellipse" && this.startPt && this.currentPt) {
      const box = this.resolveBoundingBox(this.startPt, this.currentPt);
      if (box.width > 1 && box.height > 1) {
        const tmp = new scope.Path.Ellipse(
          new scope.Rectangle(
            new scope.Point(box.left, box.top),
            new scope.Size(box.width, box.height),
          ),
        );
        pathData = tmp.pathData;
        tmp.remove();
      }
    } else if (this.kind === "freehand" && this.buffer.length > 2) {
      // Keep the trace so the part can be RE-smoothed later (#1016), and seed
      // the geometry with the same derive the commit pass will re-run, so the
      // very first render already matches.
      freehandTrace = this.buffer.map((p) => ({ x: p.x, y: p.y }));
      pathData = smoothPathFromRawPoints(
        freehandTrace,
        this.freehandSmoothness,
        true,
        scope,
      );
    } else if (this.kind === "slot" && this.startPt && this.currentPt) {
      pathData = this.buildSlotPath(this.startPt, this.currentPt);
    }
    this.cleanupPreview();
    this.startPt = null;
    this.currentPt = null;
    this.buffer = [];
    this.lastSample = null;
    this.lastP = null;
    this.drawFromCenter = false;
    setAngleHint(null);
    this.ctx.scene.setDrawSnapPoint(null);
    this.ctx.scene.clearGuideRays();
    if (pathData) {
      const piece = this.newPart(
        nextPartName(this.ctx.history.state),
        pathData,
      );
      if (freehandTrace) {
        piece.rawPoints = freehandTrace;
        piece.smoothness = this.freehandSmoothness;
      }
      // A rectangle drawn with a corner radius (#1069) comes out rounded: the
      // same `cornerRounding` the Round tool writes, re-derived on commit.
      if (rounded && this.drawCornerRadius) {
        const rounding = roundAllCorners(
          pathData,
          this.drawCornerRadius,
          scope,
        );
        if (rounding) piece.cornerRounding = rounding;
      }
      this.snapTargets = null; // geometry changed — rebuild targets next use
      // The new part lands exactly where it was drawn — no auto-arrange to
      // avoid overlapping existing parts (#738). Stay on the Shape tool after
      // every part (including the first) so the user can keep drawing —
      // overlapping parts are allowed, so there's no reason to switch to Select.
      this.ctx.commit("Create part", (proj) => withPiece(proj, piece));
      this.ctx.setSelection({ pieceId: piece.id, decorationId: null });
    }
  }

  override onCancel() {
    this.offset.cancel();
    this.cleanupPreview();
    this.cancelDraft();
    this.buffer = [];
    this.startPt = null;
    this.currentPt = null;
    this.lastSample = null;
    this.lastP = null;
    this.drawFromCenter = false;
    setAngleHint(null);
    this.ctx.scene.setDrawSnapPoint(null);
    this.ctx.scene.clearGuideRays();
  }

  /** Modifier-key state changed (Shift / Alt) while a drag /
   *  polygon hover is in flight — replay `onMove` with the new
   *  modifier values so the live preview re-evaluates size-snap
   *  (rect → square / ellipse → circle) and angle-snap (slot
   *  axis) immediately. No-op if the user isn't actively
   *  drawing. */
  override onModifiersChanged(modifiers: {
    shiftKey: boolean;
    ctrlKey: boolean;
    metaKey: boolean;
    altKey: boolean;
  }) {
    if (!this.lastSample || !this.lastP) return;
    if (
      !this.startPt &&
      this.drawEngine.vertexCount === 0 &&
      !this.polyGesture.stageActive
    )
      return;
    const synth: PointerSample = { ...this.lastSample, ...modifiers };
    if (
      this.kind === "polygon" &&
      (this.drawEngine.vertexCount > 0 || this.polyGesture.stageActive)
    ) {
      this.onHover?.(synth, this.lastP);
    } else {
      this.onMove?.(synth, this.lastP);
    }
  }

  /** Regular-polygon drag-out mode (#928): the polygon kind switches from
   *  click-to-add to a rect-style drag when the Equilateral toggle is on OR
   *  the size / centre snap is active. Decided at press; never engages
   *  mid-draft. */
  private regularPolygonMode(s: PointerSample): boolean {
    if (this.kind !== "polygon") return false;
    return (
      this.ctx.settings.polygonEquilateral ||
      this.shouldSnapSizes(s) ||
      this.shouldSnapCenter(s)
    );
  }

  private updatePreview() {
    const scope = this.ctx.scene.getPaperScope();
    this.cleanupPreview();
    if (!this.startPt || !this.currentPt) return;
    if (this.kind === "polygon") {
      // Regular-polygon drag feedback (#928/#931/#934): the shared gesture
      // renders the ghost (via `renderPolygonGhost`), the first-side angle
      // ray, and the live panel mirrors.
      this.polyGesture.dragFeedback(
        this.lastSample,
        { x: this.startPt.x, y: this.startPt.y },
        { x: this.currentPt.x, y: this.currentPt.y },
        this.drawFromCenter,
      );
      return;
    } else if (this.kind === "rect") {
      const box = this.resolveBoundingBox(this.startPt, this.currentPt);
      if (box.width > 0 && box.height > 0) {
        // Preview the draw-time corner radius the commit applies (#1069),
        // under the same rules as `roundAllCorners`: nothing below 0.5 mm,
        // and capped at half the shorter side (Paper clamps it there too).
        const rect = new scope.Rectangle(
          new scope.Point(box.left, box.top),
          new scope.Size(box.width, box.height),
        );
        const r =
          this.drawCornerRadius != null && this.drawCornerRadius >= 0.5
            ? this.drawCornerRadius
            : null;
        this.previewItem = r
          ? new scope.Path.Rectangle(rect, new scope.Size(r, r))
          : new scope.Path.Rectangle(rect);
        this.dimensionLabel.update(scope, this.ctx.viewport.view, box);
      }
    } else if (this.kind === "ellipse") {
      const box = this.resolveBoundingBox(this.startPt, this.currentPt);
      if (box.width > 0 && box.height > 0) {
        this.previewItem = new scope.Path.Ellipse(
          new scope.Rectangle(
            new scope.Point(box.left, box.top),
            new scope.Size(box.width, box.height),
          ),
        );
        this.dimensionLabel.update(scope, this.ctx.viewport.view, box);
      }
    } else if (this.kind === "freehand" && this.buffer.length > 1) {
      const path = new scope.Path({
        segments: this.buffer.map((b) => new scope.Point(b.x, b.y)),
      });
      this.previewItem = path;
    } else if (this.kind === "slot") {
      const data = this.buildSlotPath(this.startPt, this.currentPt);
      if (data) {
        this.previewItem = new scope.CompoundPath({ pathData: data });
      }
    }
    if (this.previewItem) {
      (this.previewItem as paper.Path).strokeColor = new scope.Color(
        cssVar("--canvas-accent"),
      );
      (this.previewItem as paper.Path).strokeWidth = 0.75;
      (this.previewItem as paper.Path).dashArray = [4, 2];
      (this.previewItem as paper.Path).fillColor = null;
      this.previewItem.data = { ephemeral: true };
    }
  }

  // ---- DrawGestureHost implementation (#804 IE3) --------------------------
  // ShapeTool owns a `DrawGestureEngine` (`this.drawEngine`) that runs the
  // generic click-to-add polygon flow. Construction-only capabilities are off;
  // the leather vertex/grid snap + the piece commit are injected here.

  getScope(): paper.PaperScope {
    return this.ctx.scene.getPaperScope();
  }
  getScene(): Scene {
    return this.ctx.scene;
  }
  previewColor(): string {
    return cssVar("--canvas-accent");
  }
  isPolygon(): boolean {
    return this.kind === "polygon";
  }
  isPolyKind(): boolean {
    return this.kind === "polygon";
  }
  /** Leather has no line continuation (#328). */
  get continuationEnabled(): boolean {
    return false;
  }
  /** Leather has no per-vertex bezier handles (#217). */
  get bezierEnabled(): boolean {
    return false;
  }
  seedContinuation(): boolean {
    return false;
  }
  /** Leather previews don't light a mid-point self-snap halo. */
  selfSnapAt(): { pos: paper.Point; index: number } | null {
    return null;
  }

  /** DrawGestureHost: resolve a placed vertex through the leather snap tiers
   *  (vertex › angle › grid). Extracted from the old `onDown` polygon branch. */
  resolveDraftPlacement(
    s: PointerSample,
    p: { x: number; y: number },
  ): paper.Point {
    const scope = this.ctx.scene.getPaperScope();
    const snap = this.snap(p);
    if (snap.hit?.kind === "vertex") {
      // Vertex snap wins over angle snap — land the click on the corner.
      setAngleHint(null);
      return new scope.Point(snap.point.x, snap.point.y);
    }
    if (this.shouldSnapAngle(s) && this.drawEngine.points.length > 0) {
      const last = this.drawEngine.points[this.drawEngine.points.length - 1]!;
      const sn = snapToCommonAngle(last, p);
      setAngleHint(sn.angleRad);
      return new scope.Point(sn.x, sn.y);
    }
    return new scope.Point(snap.point.x, snap.point.y);
  }

  /** DrawGestureHost: resolve the rubber-band hover through the same snap tiers,
   *  plus close-snap onto the first vertex. Extracted from the old `onHover`
   *  polygon branch. */
  resolveDraftHover(
    s: PointerSample,
    p: { x: number; y: number },
  ): paper.Point {
    const scope = this.ctx.scene.getPaperScope();
    const snap = this.snap(p);
    const pts = this.drawEngine.points;
    const last = pts[pts.length - 1]!;
    let pt: { x: number; y: number } = p;
    if (snap.hit?.kind === "vertex") {
      pt = snap.point;
      setAngleHint(null);
    } else if (this.shouldSnapAngle(s)) {
      const sn = snapToCommonAngle(last, p);
      pt = { x: sn.x, y: sn.y };
      setAngleHint(sn.angleRad);
    } else {
      if (snap.hit?.kind === "grid") pt = snap.point;
      setAngleHint(null);
    }
    // Close-snap: hovering near the first vertex (≥ 3 placed) snaps the pending
    // point home so the loop previews closed and the click commits (#779). The
    // engine's `isHoveringToClose` then drives the closed preview + halo.
    const first = pts[0]!;
    const closing =
      pts.length >= 3 &&
      first.getDistance(new scope.Point(p.x, p.y)) <
        closeSnapRadius(scope.view?.zoom ?? 1);
    if (closing) {
      pt = { x: first.x, y: first.y };
      setAngleHint(null);
      this.ctx.scene.setDrawSnapPoint({ x: first.x, y: first.y });
    }
    return new scope.Point(pt.x, pt.y);
  }

  /** DrawGestureHost: `onHover` while the draft is still empty — light the
   *  first-click snap indicator. */
  onEmptyDraftHover(_s: PointerSample, p: { x: number; y: number }): void {
    this.snap(p);
  }

  /** DrawGestureHost: build the thin dashed leather preview + close halo. */
  buildPreview(group: paper.Group, closing: boolean): void {
    const scope = this.ctx.scene.getPaperScope();
    const stroke = this.previewColor();
    const pts = this.drawEngine.points;
    const hover = this.drawEngine.hoverPt;
    // While closing, drop the trailing hover point (snapped onto the first
    // vertex) and render the loop closed so the user sees what will commit.
    const segs = closing ? [...pts] : hover ? [...pts, hover] : [...pts];
    const path = new scope.Path({ segments: segs });
    if (closing) path.closed = true;
    path.strokeColor = new scope.Color(stroke);
    path.strokeWidth = 0.75;
    path.dashArray = [4, 2];
    path.fillColor = null;
    group.addChild(path);
    // Halo on the first vertex marks the close target while the cursor is in
    // its snap zone (mirrors the construction / stitch close affordance).
    if (closing) {
      const first = pts[0]!;
      const halo = new scope.Path.Circle(first, CLOSE_SNAP_RADIUS);
      halo.strokeColor = new scope.Color(stroke);
      halo.strokeColor.alpha = 0.6;
      halo.strokeWidth = 0.6;
      halo.strokeScaling = false;
      halo.fillColor = null;
      group.addChild(halo);
    }
  }

  /** DrawGestureHost: commit the current polygon draft into a new part. Needs
   *  ≥ 3 vertices to enclose an area; with fewer, the draft is discarded. */
  commitDraft(_closed: boolean): void {
    const pts = this.drawEngine.points;
    if (pts.length >= 3) {
      const buf = pts.map((p) => ({ x: p.x, y: p.y }));
      const data = pathFromBuffer(buf) + " Z";
      const piece = this.newPart(nextPartName(this.ctx.history.state), data);
      this.snapTargets = null; // geometry changed — rebuild targets next use
      // Land the polygon where it was drawn — no auto-arrange (#738). Stay on
      // the Shape tool so the user can keep drawing (overlaps are allowed).
      this.ctx.commit("Create polygon", (proj) => withPiece(proj, piece));
      this.ctx.setSelection({ pieceId: piece.id, decorationId: null });
    }
    this.cancelDraft();
  }

  /** DrawGestureHost: full draft reset — clear the engine + this tool's own
   *  preview / label / snap + ray indicators. */
  cancelDraft(): void {
    this.polyGesture.cancel();
    this.drawEngine.reset();
    this.cleanupPreview();
    setAngleHint(null);
    this.ctx.scene.setDrawSnapPoint(null);
    this.ctx.scene.clearGuideRays();
    this.drawEngine.notifyDraftChange();
  }

  /** Observe polygon-draft vertex-count changes so the on-canvas finish/cancel
   *  fly-out (#779) can show/hide. Delegates to the engine. */
  setDraftChangeListener(fn: ((vertexCount: number) => void) | null) {
    this.drawEngine.setDraftChangeListener(fn);
  }

  /** Number of polygon vertices placed (0 = no draft). Drives the fly-out and
   *  the Enter / Escape handling; stays 0 for the drag-out kinds. Regular-
   *  polygon stages (#931/#934) surface as 2 (✓ enabled) / 1 (✓ disabled). */
  get vertexCount(): number {
    if (this.kind !== "polygon") return 0;
    const staged = this.polyGesture.vertexCount();
    return staged > 0 ? staged : this.drawEngine.vertexCount;
  }

  /** Commit the in-progress polygon (Enter / ✓ / double-click). Delegates to
   *  the engine (commit ≥ 3 vertices, else discard). Returns true when there
   *  was a draft to act on so the caller can preventDefault. */
  handleFinish(): boolean {
    if (this.kind !== "polygon") return false;
    // Regular-polygon stages (#931/#934): Enter commits the staged preview.
    if (this.polyGesture.finish()) return true;
    return this.drawEngine.handleFinish();
  }

  /** Discard the in-progress polygon (Escape / ✗). Returns true when handled. */
  handleDiscard(): boolean {
    if (this.kind !== "polygon") return false;
    if (this.polyGesture.escape()) return true;
    if (this.drawEngine.vertexCount === 0) return false;
    this.cancelDraft();
    return true;
  }

  /** Double-click finishes the polygon, like the other draw tools. */
  handleDoubleClick(): boolean {
    return this.handleFinish();
  }

  private cleanupPreview() {
    if (this.previewItem) {
      this.previewItem.remove();
      this.previewItem = null;
    }
    this.dimensionLabel.remove();
  }

  /** Resolve the bounding box for a rect/ellipse drag — delegates to the
   *  shared {@link resolveDragBox}, threading the preset width / height and
   *  the latched "draw from centre" flag (Alt / centre snap toggle). */
  private resolveBoundingBox(
    start: paper.Point,
    current: paper.Point,
  ): { left: number; top: number; width: number; height: number } {
    return resolveDragBox(
      { x: start.x, y: start.y },
      { x: current.x, y: current.y },
      {
        presetWidth: this.presetWidth,
        presetHeight: this.presetHeight,
        fromCenter: this.drawFromCenter,
      },
    );
  }

  /** Build the stadium / capsule path for the "slot" (pill) kind. Mirrors
   *  CutoutTool's buildSlotPath: a click without a meaningful drag produces
   *  a default-length horizontal pill so single-click placement still works. */
  private buildSlotPath(start: paper.Point, end: paper.Point): string | null {
    const w = this.slotWidth;
    if (start.getDistance(end) < 0.5) {
      // A click without a drag already places the default pill centred on
      // the press point, so it reads the same with or without from-centre.
      const halfL = (w * SLOT_DEFAULT_LENGTH_RATIO) / 2;
      return makeSlotPathData(
        { x: start.x - halfL, y: start.y },
        { x: start.x + halfL, y: start.y },
        w,
      );
    }
    // From-centre (Alt / centre snap) treats the press point as the
    // capsule's midpoint instead of one cap (issue #389).
    const { a, b } = resolveDragAxis(start, end, this.drawFromCenter);
    return makeSlotPathData(a, b, w);
  }
  /** Offset kind (#972): a typed distance fixes the inset / outset magnitude so
   *  a click on an edge commits at exactly that value; null → drag-defined. */
  setPresetOffsetDistance(mm: number | null): void {
    this.offset.setPresetDistance(mm);
  }

  /** Rounded-corner preference for the next outline offset (#423) — the
   *  tool side of the same choice the construction Offset sub-tool has. */
  setOffsetRoundedCorners(on: boolean): void {
    this.offset.setRoundedCorners(on);
  }

  /** Offset kind (#972): mirror the live drag distance back into the panel. */
  setLiveOffsetDistanceCallback(fn: ((mm: number) => void) | undefined): void {
    this.offset.setLiveDistanceCallback(fn);
  }

  // -------------------------------------------------------------------
  // OutlineOffsetHost (#972) — the Offset shape kind
  // -------------------------------------------------------------------

  // `getScope` / `getScene` are already provided for `DrawGestureHost` above —
  // the two host interfaces overlap, so one implementation serves both.

  /** Leather mode offsets the shared source set (`offsetSourceOutlines`) with
   *  BOTH directions legal from every source — inward for a smaller part,
   *  outward for a larger one — because a part has no containment rule to
   *  satisfy, unlike a cutout. Offsetting a cutout's edge here is how you get a
   *  part shaped like the hole: a plug, or a matching inlay. */
  offsetCandidates(): OffsetCandidate[] {
    return offsetSourceOutlines(this.ctx.history.state).map((src) => ({
      ...src,
      allowInside: true,
      allowOutside: true,
    }));
  }

  /** A new part, with its anchor locks seeded the way construction seeds a
   *  new line (#1099): an anchor that is ALREADY curved (ellipse, freehand,
   *  slot arc) starts locked smooth, a straight corner gets no entry.
   *
   *  Leather used to seed nothing, and the outline editor guessed the lock
   *  from the handle geometry instead. That guess could not tell "no lock
   *  recorded" from "deliberately unlocked", so the bezier cycle's
   *  independent-handles state — two mirrored handles and no entry — read back
   *  as smooth, and the first click on a sharp corner behaved like the third. */
  private newPart(name: string, pathData: string): Piece {
    const anchors = computeDefaultSmoothAnchors(
      pathData,
      this.ctx.scene.getPaperScope(),
    );
    const piece = createPiece(name, pathData);
    return anchors ? { ...piece, smoothAnchors: anchors } : piece;
  }

  commitOffset(_source: OffsetCandidate, pathData: string): void {
    const piece = this.newPart(nextPartName(this.ctx.history.state), pathData);
    this.snapTargets = null; // geometry changed — rebuild targets next use
    this.ctx.commit("Offset part", (proj) => withPiece(proj, piece));
    this.ctx.setSelection({ pieceId: piece.id, decorationId: null });
  }

  onOffsetTooDeep(): void {
    showCanvasToast("Offset too deep — the outline collapses");
  }
}
